import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  TrendingUp,
  GripVertical,
  CheckCircle,
  AlertCircle,
  Trophy,
  Users,
  Lightbulb,
  ArrowUp,
  ArrowDown,
  Send,
  Star,
} from 'lucide-react';

// Feature priority items (Champions can rank these)
interface FeatureItem {
  id: string;
  title: string;
  description: string;
  category: string;
  championVotes: number;
  residentVotes: number;
  currentRank: number;
  status: 'planned' | 'in-progress' | 'coming-soon';
}

const initialFeatures: FeatureItem[] = [
  {
    id: 'feat-1',
    title: 'Installable PWA (Web)',
    description: 'Add to home screen for app-like access without app stores',
    category: 'Platform',
    championVotes: 27,
    residentVotes: 145,
    currentRank: 1,
    status: 'in-progress',
  },
  {
    id: 'feat-2',
    title: 'Automatic HOA Fee Payments',
    description: 'Set up recurring payments for HOA/community fees',
    category: 'Financial',
    championVotes: 24,
    residentVotes: 132,
    currentRank: 2,
    status: 'planned',
  },
  {
    id: 'feat-3',
    title: 'Emergency Notifications',
    description: 'Instant alerts for urgent community issues (weather, safety, etc.)',
    category: 'Safety',
    championVotes: 21,
    residentVotes: 118,
    currentRank: 3,
    status: 'planned',
  },
  {
    id: 'feat-4',
    title: 'Neighborhood Calendar',
    description: 'Shared calendar for events, meetings, and important dates',
    category: 'Social',
    championVotes: 18,
    residentVotes: 89,
    currentRank: 4,
    status: 'planned',
  },
  {
    id: 'feat-5',
    title: 'Vendor Rating System',
    description: 'Rate and review vendors who work in the neighborhood',
    category: 'Services',
    championVotes: 15,
    residentVotes: 76,
    currentRank: 5,
    status: 'planned',
  },
  {
    id: 'feat-6',
    title: 'Document Library',
    description: 'Central repository for HOA docs, meeting minutes, bylaws',
    category: 'Governance',
    championVotes: 12,
    residentVotes: 54,
    currentRank: 6,
    status: 'coming-soon',
  },
];

const statusColors = {
  planned: 'bg-blue-100 text-blue-800 border-blue-200',
  'in-progress': 'bg-green-100 text-green-800 border-green-200',
  'coming-soon': 'bg-purple-100 text-purple-800 border-purple-200',
};

const statusLabels = {
  planned: 'Planned',
  'in-progress': 'In Progress',
  'coming-soon': 'Coming Soon',
};

export function PriorityVoting() {
  const [features, setFeatures] = useState<FeatureItem[]>(initialFeatures);
  const [suggestTitle, setSuggestTitle] = useState('');
  const [suggestDescription, setSuggestDescription] = useState('');
  const [suggestCategory, setSuggestCategory] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Move feature up in ranking
  const moveUp = (index: number) => {
    if (index === 0) return;
    const newFeatures = [...features];
    [newFeatures[index], newFeatures[index - 1]] = [newFeatures[index - 1], newFeatures[index]];
    setFeatures(newFeatures);
    setHasChanges(true);
  };

  // Move feature down in ranking
  const moveDown = (index: number) => {
    if (index === features.length - 1) return;
    const newFeatures = [...features];
    [newFeatures[index], newFeatures[index + 1]] = [newFeatures[index + 1], newFeatures[index]];
    setFeatures(newFeatures);
    setHasChanges(true);
  };

  // Save ranking
  const handleSaveRanking = async () => {
    setIsSubmitting(true);
    try {
      // TODO: Submit to backend
      // const response = await submitChampionRanking({ rankings: features.map((f, i) => ({ id: f.id, rank: i + 1 })) });

      // Mock submission
      await new Promise((resolve) => setTimeout(resolve, 800));

      console.log(
        'Rankings saved:',
        features.map((f, i) => ({ id: f.id, rank: i + 1, title: f.title })),
      );

      setHasChanges(false);
      alert(
        'Preview only. This feature is not connected yet, so your rankings were not saved. Thank you for trying it out.',
      );
    } catch (err) {
      console.error('Save error:', err);
      alert('Failed to save rankings. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle suggest new feature
  const handleSuggestFeature = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!suggestTitle.trim()) {
      setError('Please enter a feature title');
      return;
    }
    if (!suggestDescription.trim() || suggestDescription.trim().split(/\s+/).length < 10) {
      setError('Please provide a detailed description (at least 10 words)');
      return;
    }
    if (!suggestCategory.trim()) {
      setError('Please enter a category');
      return;
    }

    setIsSubmitting(true);

    try {
      // TODO: Submit to backend
      // const response = await submitFeatureSuggestion({ title: suggestTitle, description: suggestDescription, category: suggestCategory });

      // Mock submission
      await new Promise((resolve) => setTimeout(resolve, 800));

      console.log('Feature suggested:', {
        title: suggestTitle,
        description: suggestDescription,
        category: suggestCategory,
      });

      setSubmitSuccess(true);
      setSuggestTitle('');
      setSuggestDescription('');
      setSuggestCategory('');
      setTimeout(() => setSubmitSuccess(false), 3000);
    } catch (err) {
      setError('Failed to submit suggestion. Please try again.');
      console.error('Submission error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Calculate total votes (Champion votes = 3x weight)
  const getTotalVotes = (feature: FeatureItem) => {
    return feature.championVotes * 3 + feature.residentVotes;
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-3xl font-bold mb-2">Feature Priority Voting</h1>
        <p className="text-muted-foreground">
          Rank features by importance. Your vote counts 3x more than regular residents!
        </p>
      </div>

      {/* Champion Vote Power Alert */}
      <Alert className="bg-primary/5 border-primary/20">
        <Trophy className="h-4 w-4 text-primary" />
        <AlertDescription>
          <strong>Champion Vote Power:</strong> Your ranking carries 3x the weight of a regular
          resident vote. Use this power wisely to represent your neighbors' needs!
        </AlertDescription>
      </Alert>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">{features.length}</p>
                <p className="text-sm text-muted-foreground">Features to Rank</p>
              </div>
              <TrendingUp className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">3x</p>
                <p className="text-sm text-muted-foreground">Vote Weight</p>
              </div>
              <Star className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-2xl font-bold">
                  {features.filter((f) => f.status === 'in-progress').length}
                </p>
                <p className="text-sm text-muted-foreground">In Progress</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Ranking Interface */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Rank Features</CardTitle>
              <CardDescription>
                Drag or use arrows to reorder features by your priority. Top = most important.
              </CardDescription>
            </div>
            {hasChanges && (
              <Button onClick={handleSaveRanking} disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : 'Save Rankings'}
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {features.map((feature, index) => (
              <Card key={feature.id} className="hover:shadow-md transition-shadow border-2">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-4">
                    {/* Rank Number */}
                    <div className="flex flex-col items-center gap-1 pt-1">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                          index === 0
                            ? 'bg-yellow-100 text-yellow-800'
                            : index === 1
                              ? 'bg-gray-100 text-gray-800'
                              : index === 2
                                ? 'bg-orange-100 text-orange-800'
                                : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {index + 1}
                      </div>
                      {index === 0 && <Trophy className="h-4 w-4 text-yellow-600" />}
                    </div>

                    {/* Feature Info */}
                    <div className="flex-1 space-y-2">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-lg">{feature.title}</h3>
                          <p className="text-sm text-muted-foreground mt-1">
                            {feature.description}
                          </p>
                        </div>
                        <Badge className={statusColors[feature.status]}>
                          {statusLabels[feature.status]}
                        </Badge>
                      </div>

                      <div className="flex items-center gap-4 text-sm">
                        <Badge variant="outline">{feature.category}</Badge>
                        <div className="flex items-center gap-1">
                          <Star className="h-4 w-4 text-primary" />
                          <span className="font-medium">{feature.championVotes}</span>
                          <span className="text-muted-foreground">Champion votes</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Users className="h-4 w-4 text-muted-foreground" />
                          <span>{feature.residentVotes}</span>
                          <span className="text-muted-foreground">Resident votes</span>
                        </div>
                        <div className="flex items-center gap-1 ml-auto">
                          <TrendingUp className="h-4 w-4 text-green-600" />
                          <span className="font-medium text-green-600">
                            {getTotalVotes(feature)} total weight
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Move Buttons */}
                    <div className="flex flex-col gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => moveUp(index)}
                        disabled={index === 0}
                        className="h-8 w-8 p-0"
                      >
                        <ArrowUp className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => moveDown(index)}
                        disabled={index === features.length - 1}
                        className="h-8 w-8 p-0"
                      >
                        <ArrowDown className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Suggest New Feature */}
      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-primary" />
            <CardTitle>Suggest a New Feature</CardTitle>
          </div>
          <CardDescription>Have an idea that's not on the list? Suggest it here!</CardDescription>
        </CardHeader>
        <CardContent>
          {submitSuccess ? (
            <Alert className="bg-green-50 border-green-200">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <AlertDescription className="text-green-800">
                Preview only. This form is not connected yet, so your suggestion was not sent or
                stored.
              </AlertDescription>
            </Alert>
          ) : (
            <form onSubmit={handleSuggestFeature} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <Label htmlFor="suggest-title">Feature Title *</Label>
                <Input
                  id="suggest-title"
                  value={suggestTitle}
                  onChange={(e) => setSuggestTitle(e.target.value)}
                  placeholder="E.g., Pet Registry for Lost Animals"
                  maxLength={100}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="suggest-description">Description *</Label>
                <Textarea
                  id="suggest-description"
                  value={suggestDescription}
                  onChange={(e) => setSuggestDescription(e.target.value)}
                  placeholder="Describe the feature and why it would be valuable for our community (at least 10 words)"
                  rows={5}
                />
                <p className="text-xs text-muted-foreground">
                  {
                    suggestDescription
                      .trim()
                      .split(/\s+/)
                      .filter((w) => w).length
                  }{' '}
                  words (minimum 10)
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="suggest-category">Category *</Label>
                <Input
                  id="suggest-category"
                  value={suggestCategory}
                  onChange={(e) => setSuggestCategory(e.target.value)}
                  placeholder="E.g., Social, Safety, Financial, Platform, etc."
                  maxLength={50}
                />
              </div>

              <Button type="submit" disabled={isSubmitting} className="w-full">
                {isSubmitting ? (
                  'Submitting...'
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Submit Suggestion
                  </>
                )}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>

      {/* Tips */}
      <Alert>
        <TrendingUp className="h-4 w-4" />
        <AlertDescription>
          <strong>Voting Strategy:</strong> Talk to your neighbors before ranking! Their priorities
          should guide your votes. Features at the top get built first, so choose wisely.
        </AlertDescription>
      </Alert>
    </div>
  );
}
